import React, {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { UserProfile } from 'oidc-client-ts';

interface AuthenticationProviderProps {
  setLoading: Dispatch<SetStateAction<boolean>>;
}

interface IAuthenticationContext {
  user: UserProfile | undefined;
  getAccessToken: () => Promise<string>;
  isAuthenticated: boolean;
}

export const AuthenticationContext = createContext<IAuthenticationContext>({
  user: undefined,
  getAccessToken: async () => '',
  isAuthenticated: false,
});

const AuthenticationProvider: FC<PropsWithChildren<AuthenticationProviderProps>> = ({
  setLoading,
  children,
}) => {
  const [user, setUser] = useState<UserProfile | undefined>(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    window.auth
      .getUser()
      .then((oidcUser) => {
        if (cancelled) return;
        if (!oidcUser || oidcUser.expired) {
          // auth-sdk.ts establishes the session before React renders, so reaching
          // this branch means something went wrong upstream. Surface an error
          // instead of triggering another /oauth2/authorize from this route.
          setAuthError(new Error('FusionAuth session not established'));
          setLoading(false);
          return;
        }
        setUser(oidcUser.profile);
        setIsAuthenticated(true);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setAuthError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [setLoading]);

  const getAccessToken = async (): Promise<string> => window.auth.getAccessToken();

  if (authError) throw authError;

  return (
    <AuthenticationContext.Provider value={{ user, isAuthenticated, getAccessToken }}>
      {children}
    </AuthenticationContext.Provider>
  );
};

const useAuthenticationContext = () => useContext(AuthenticationContext);
export { AuthenticationProvider, useAuthenticationContext };
